import * as React from "react";
import TeamsBaseComponent, {ITeamsBaseComponentState} from "msteams-react-base-component";
import {DmsCaseInfo, DmsStorageType, FilingLocation, FilterOption, FilterOptionsResponse,PaginatedFilingLocations, PagingToken} from "../../models";
import * as microsoftTeams from "@microsoft/teams-js";
import {get, post} from "../../api";
import {TeamsApiService} from "../../services/teams/TeamsApiService";
import { ErrorHandler } from "../../errors/ErrorHandler";

export interface IDocumentsState extends ITeamsBaseComponentState {
    documents: {[key: string]: PaginatedFilingLocations};
    currentFolder: FilingLocation;
    currentPath: string;
    caseInfo: DmsCaseInfo;
    filterOptions: FilterOption[];
    selectedFilters: FilterOption[];
}

export const BrowseDocumentsContext: React.Context<BrowseContextProps> = React.createContext({
    theme: null,
    currentPath: "",
    documents: {},
    currentFolder: null,
    caseInfo: null,
    filterOptions: null,
    selectedFilters: null,
    browseDms: () => Promise.resolve(),
    isListLimited: (_: string, _2: string) => false,
    setParentState: () => {},
    getDocumentsByPath: (_: string) => null,
    getFilterOptions: () => Promise.resolve(),
    filterDocuments: () => Promise.resolve(),
    getCacheKey: () => "",
});

export interface BrowseContextProps {
    browseDms: (request: BrowseDmsRequest, cacheKey?: string) => Promise<void>;
    isListLimited: (path: string, storageType: string) => boolean;
    setParentState: (state: Partial<IDocumentsState>, callback?: () => void) => void;
    getDocumentsByPath: (path: string) => PaginatedFilingLocations;
    getFilterOptions: () => Promise<void>;
    filterDocuments: (request: Partial<BrowseDmsRequest>) => Promise<void>;
    getCacheKey: (folderSearch: boolean, loadRootFolders: boolean) => string;
    caseInfo: DmsCaseInfo;
    currentPath: string;
    filterOptions: FilterOption[];
    selectedFilters: FilterOption[];
}

export interface IDocumentsProps {}

export interface BrowseDmsRequest {
    Path: string;
    ContentLocationId?: number;
    Metadata: MetadataSearch;
    FolderSearch?: boolean;
    AttachmentType?: string;
    StorageType: string;
    RootUrl: string;
    TeamsId: string;
    TenantId: string;
    UserId: string;
    ChannelId: string;
    SpHostKVPrefix?: string;
    BrowsingDms?: boolean;
    ExternalChannelId?: string;
    ExternalTeamId?: string;
    NextPage: PagingToken;
    SortProperties?: {[key: string]: boolean};
    FilterParameters?: FilterOption[];
    UsePaging?: boolean;
    handleResult: (response: FilingLocation[], loadedAllPages: boolean) => void;
    handleError: (errorMessage: string) => void;
    RefreshingView: boolean;
}

interface MetadataSearch {
    LocalFilesBrowse?: boolean;
    StorageType?: string;
    DmsSiteUrl?: string;
    UserId?: string;
    TenantId?: string;
    LinkedMatter?: string;
    GetRootFolders?: string;
}

export class BrowseDocuments extends TeamsBaseComponent<IDocumentsProps, IDocumentsState> {
    teamsApiService = new TeamsApiService();
    errorHandler = new ErrorHandler();

    constructor(props: IDocumentsProps) {
        super(props);
        this.state = {
            theme: null,
            documents: {},
            currentFolder: null,
            currentPath: "",
            caseInfo: null,
            filterOptions: null,
            selectedFilters: null,
        };
    }

    public async componentWillMount(): Promise<void> {
        microsoftTeams.initialize();
    }

    public GetCachedDmsDocumentsFetchFallback = async (request: BrowseDmsRequest, cacheKey?: string) => {
        const isLoadingSubfolder = !!this.state.currentFolder;
        request.Path = this.getCurrentPath();
        
        let loadRootFolders = false;

        if (isLoadingSubfolder) {
            // loading a sub-folder
            if (request.Metadata === null) {
                request = {
                    ...request,
                    Metadata: this.state.currentFolder.Metadata,
                    SpHostKVPrefix: this.state.currentFolder.SpHostKVPrefix,
                    RootUrl: this.state.currentFolder.SiteUrl,
                    ExternalChannelId: this.state.currentFolder.ExternalChannelId,
                    ExternalTeamId: this.state.currentFolder.ExternalTeamId,
                };
            }
        } else {
            // loading root folder
            const caseDetails = JSON.parse(this.state.caseInfo.CaseDetails);
            request = {
                ...request,
                Metadata: {GetRootFolders: "Yes"},
                RootUrl: caseDetails.SiteUrl,
                SpHostKVPrefix: caseDetails.SpHostKVPrefix,
            };
            loadRootFolders = true;
        }

        this.setState({currentPath: request.Path}, async () => {
            if (cacheKey == null) cacheKey = this.getCacheKey(request.FolderSearch, loadRootFolders);

            let filteredDocuments: FilingLocation[] = [];
            if (this.state.documents[cacheKey]) filteredDocuments = this.state.documents[cacheKey].Page;

            if (request.StorageType !== DmsStorageType.SharePoint && filteredDocuments.length > 0) {
                // if not sharepoint and cached return cached and refetch in background
                // if pagination implemented for netdocs/imanage, don't call handleResult here to prevent duplicate requests
                request.handleResult(filteredDocuments, false);
            }

            this.BrowseDmsLocations(request, cacheKey);
        });
    };

    public setCurrentPath = (url: string) => {
        this.setState({currentPath: url});
    };

    public isListLimited = (path: string, storageType: string) => {
        if (storageType === DmsStorageType.SharePoint) {
            const documents = this.state.documents[path];
            return documents && documents.PagingToken.TotalItemCount > 5000;
        }

        return false;
    };

    public setCaseInfo = (caseInfo: DmsCaseInfo) => {
        this.setState({caseInfo});
    };

    public getDocumentsByPath = (path: string) => {
        return this.state.documents[path];
    };

    public getFilterOptions = async () => {
        const caseDetails = JSON.parse(this.state.caseInfo.CaseDetails);
        const url = `/api/teamsdmsintegration/GetFilterOptions?storageType=${
            this.state.caseInfo.StorageType
        }&path=${this.getCurrentPath()}&rootUrl=${caseDetails.SiteUrl}&tenantId=${this.getQueryVariable(
            "tenantId"
        )}&teamsId=${this.getQueryVariable("teamsId")}&spHostKvPrefix=${caseDetails.SpHostKVPrefix}`;

        await get(url)
            .then((r) => r.json())
            .then((response: FilterOptionsResponse) => {
                this.setState({filterOptions: response.FilterOptions});
            });
    };

    public filterDocuments = async (request: BrowseDmsRequest) => {
        const cacheKey = this.getCacheKey(request.FolderSearch, false);
        const documents = this.state.documents;
        if (documents[cacheKey]) {
            delete documents[cacheKey];
            this.setState({documents});
        }

        const browseDmsRequest: BrowseDmsRequest = {
            TeamsId: request.TeamsId,
            TenantId: request.TenantId,
            UserId: request.UserId,
            ChannelId: request.ChannelId,
            FolderSearch: request.FolderSearch,
            BrowsingDms: request.BrowsingDms,
            FilterParameters: request.FilterParameters,
            Path: null,
            Metadata: null,
            StorageType: this.state.caseInfo.StorageType,
            RootUrl: null,
            NextPage: null,
            handleResult: request.handleResult,
            handleError: request.handleError,
            RefreshingView: request.RefreshingView
        };

        this.GetCachedDmsDocumentsFetchFallback(browseDmsRequest, cacheKey);
    };

    public getCacheKey = (folderSearch: boolean, loadRootFolders: boolean = false): string => {
        const { caseInfo, currentPath } = this.state;
        if (caseInfo && currentPath === caseInfo.LinkedMatterUrl) {
            const rootPrefix = loadRootFolders ? "root-" : "";
            const searchType = folderSearch ? "folders" : "files";
            return `${rootPrefix}${caseInfo.LinkedMatterUrl}-${searchType}`;
        }
        return currentPath;
    };

    private getCurrentPath = () => {
        const isLoadingSubfolder = !!this.state.currentFolder;
        const path = isLoadingSubfolder ? this.state.currentFolder.Url : this.state.caseInfo.LinkedMatterUrl;

        return path;
    };

    private BrowseDmsLocations = async (request: BrowseDmsRequest, cacheKey: string) => {
        let loadingFirstPage = true;

        if (this.state.documents[cacheKey]) {
            request.NextPage = this.state.documents[cacheKey].PagingToken;
            loadingFirstPage = false;
        }
       
        const {handleResult, handleError, ...browseRequest} = request;

        if(request.FilterParameters)
        {
            var options = this.state.filterOptions.map(a => a.DisplayName);
            var selectedFilters = request.FilterParameters.map(a => a.DisplayName);
            
            const found = options.some(option=> selectedFilters.includes(option));
            if(found === false)
            {
                //Force viewing all files in the folder, if the selected filters do not exist in the browsing directory
                browseRequest.FilterParameters = null;
            }
        }

        const loadedAllPages =
            this.state.documents[cacheKey] && this.state.documents[cacheKey].Page.length === this.state.documents[cacheKey].PagingToken.TotalItemCount && 
            !request.RefreshingView;

        if (request.StorageType === DmsStorageType.SharePoint && 
            !request.FilterParameters &&
            loadedAllPages === true 
            ) {
            // no more pages to load, don't send request            
            handleResult(this.state.documents[cacheKey].Page, loadedAllPages);
            return; 
        }

        if (
            request.StorageType === DmsStorageType.SharePoint &&
            !request.FilterParameters &&
            loadingFirstPage &&
            this.state.documents[cacheKey] &&
            this.state.documents[cacheKey].Page.length > 0 &&
            !request.RefreshingView
        ) {
            // navigated to another folder then back to this folder, don't send another request until they scroll to the bottom
            handleResult(this.state.documents[cacheKey].Page, loadedAllPages);
            return;
        }

        await post("/api/teamsdmsintegration/BrowseDmsLocations", browseRequest)
            .then((r) => r.json())
            .then((response: any) => {
                if (response.Error !== undefined && response.Error !== null) {
                    this.errorHandler.HandleConfigurationErrorResponse(response, null, (errorMessage: string) => {
                        handleError(errorMessage);
                    });
                    return;
                }
                const paginatedFilingLocations = response as PaginatedFilingLocations;

                const setStateResult = this.state.documents;
                if (request.StorageType === DmsStorageType.SharePoint && 
                    setStateResult[cacheKey] && !request.RefreshingView) 
                {
                    // if dms is sharepoint and there is already a page loaded, append the new page
                    setStateResult[cacheKey].Page = setStateResult[cacheKey].Page && paginatedFilingLocations.PagingToken.PagingToken
                        ? setStateResult[cacheKey].Page.concat(paginatedFilingLocations.Page)
                        : paginatedFilingLocations.Page;
                    setStateResult[cacheKey].PagingToken = paginatedFilingLocations.PagingToken;
                } else {
                    // if dms is not sharepoint or this is the first page, set to the new page
                    setStateResult[cacheKey] = paginatedFilingLocations;
                }

                this.setState({documents: setStateResult}, () => {
                    const documentItems = this.state.documents[cacheKey].Page;
                    const loadedAllPages = documentItems.length === paginatedFilingLocations.PagingToken.TotalItemCount;
                    if (this.state.currentPath == request.Path) handleResult(documentItems, loadedAllPages);
                });
            })
            .catch((error) => {
                this.errorHandler.HandleConfigurationErrorResponse(error, null, (errorMessage: string) =>{
                    handleError(errorMessage);
                });
            });
    };

    public render() {
        return (
            <div>
                <BrowseDocumentsContext.Provider
                    value={{
                        browseDms: this.GetCachedDmsDocumentsFetchFallback,
                        isListLimited: this.isListLimited,
                        setParentState: (state: Pick<IDocumentsState, keyof IDocumentsState>, callback?: () => void) =>
                            this.setState(state, callback),
                        getDocumentsByPath: this.getDocumentsByPath,
                        getFilterOptions: this.getFilterOptions,
                        filterDocuments: this.filterDocuments,
                        getCacheKey: this.getCacheKey,
                        caseInfo: this.state.caseInfo,
                        currentPath: this.state.currentPath,
                        filterOptions: this.state.filterOptions,
                        selectedFilters: this.state.selectedFilters,
                    }}
                >
                    {this.props.children}
                </BrowseDocumentsContext.Provider>
            </div>
        );
    }
}

BrowseDocuments.contextType = BrowseDocumentsContext;
